// front/src/pages/Todos.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageLayout from '../components/PageLayout';
import TodoDetailModal from '../components/TodoDetailModal';
import TodoReassignModal from '../components/TodoReassignModal';
import { useUser } from '../context/UserContext';
import { socket } from '../socket';
import { listTodos, updateTodo } from '../api/todos';
import { showTime } from '../utils/dateHelpers';
import { Check, X, UserRound } from 'lucide-react';
import { ROLE_LABELS } from '../constants/roles';
import { patientIdentite } from '../utils/todoDisplay';
import { CONFIG } from '../config/config';

function clamp2Style() {
  return {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };
}

function showDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function todoTitre(t) {
  const nature = t.titre || 'To Do';
  const ident = patientIdentite(t.pii) || patientIdentite(t.passagePii);
  if (!ident) return nature;
  const sexe = t.pii?.sexe ? ` (${t.pii.sexe})` : '';
  return `${nature}\n${ident}${sexe}`;
}

export default function Todos() {
  const { profile, user } = useUser();
  const profileId = profile?.profile_id || null;
  const role = (user?.role || '').toLowerCase();
  const isAdmin = role === 'admin';
  const isSecretaire = role === 'secretaire' || role === 'admin';
  const canSeeTab = ['infirmiere', 'medecin', 'admin', 'secretaire'].includes(role);

  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [historyShown, setHistoryShown] = useState(false);
  const [scope, setScope] = useState('mine'); 
  const [rolesFilter, setRolesFilter] = useState([]); 
  const [detailId, setDetailId] = useState(null);
  const [reassignTodo, setReassignTodo] = useState(null);

  const [paramsTodo, setParamsTodo] = useState(null);

  useEffect(() => {
    const fetchParams = async () => {
      try {
        const res = await fetch(`${CONFIG.API_URL}/todos/paramDeftodos`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }); 
        if (!res.ok) throw new Error('Erreur réseau');
        const data = await res.json();
        setParamsTodo(data);
      } catch (e) {
        console.error('Erreur chargement paramsTodo :', e);
      }
    };
    fetchParams();
  }, []);

  // Fonction dynamique qui lit la couleur depuis le state
  const getTodoColor = (natureCode) => {
    if (!natureCode || !paramsTodo) return null;
    return paramsTodo[natureCode]?.color || null;
  };

  const load = useCallback(() => {
    setLoading(true);
    setErr('');
    return listTodos({
      profileId,
      mine: isAdmin && scope === 'mine' ? 1 : undefined,
      roles: rolesFilter,
    })
      .then((rows) => setTodos(Array.isArray(rows) ? rows : []))
      .catch((e) => {
        console.error('[todos] load failed:', e);
        setErr(e?.message || 'Impossible de charger les To Do');
      })
      .finally(() => setLoading(false));
  }, [profileId, isAdmin, scope, rolesFilter]);

  const toggleRoleFilter = (r) => {
    setRolesFilter((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let timer = null;
    const trigger = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => load(), 150);
    };
    socket.on('todoCreated', trigger);
    socket.on('todoUpdated', trigger);
    return () => {
      if (timer) clearTimeout(timer);
      socket.off('todoCreated', trigger);
      socket.off('todoUpdated', trigger);
    };
  }, [load]);

  const currentTodos = useMemo(() => {
    return todos
      .filter((t) => t.statut?.code === 'nouveau')
      .sort((a, b) => {
        if (a.urgence && !b.urgence) return -1;
        if (!a.urgence && b.urgence) return 1;
        const ta = new Date(a.created_at || 0).getTime();
        const tb = new Date(b.created_at || 0).getTime();
        return ta - tb;
      });
  }, [todos]);

  const historyTodos = useMemo(() => {
    return todos
      .filter((t) => t.statut?.code !== 'nouveau')
      .sort((a, b) => {
        const ta = new Date(a.created_at || 0).getTime();
        const tb = new Date(b.created_at || 0).getTime();
        return tb - ta; // Tri décroissant : du plus récent (tb) au plus vieux (ta)
      });
  }, [todos]);

  const actDone = async (id) => {
    try {
      await updateTodo(id, { statutCode: 'termine', profileId });
    } catch (e) {
      console.error('[todos] terminer failed:', e);
    }
  };

  const actCancel = async (id) => {
    try {
      await updateTodo(id, { statutCode: 'supprime', profileId });
    } catch (e) {
      console.error('[todos] supprimer failed:', e);
    }
  };

  if (!canSeeTab) {
    return (
      <PageLayout title="To Do">
        <div className="p-4 text-slate-600">Réservé aux soignants/admin.</div>
      </PageLayout>
    );
  }

  const TodoCard = ({ t }) => {
    const isCreator = t.profilDemandeur?.id === profileId || isAdmin;

    // Utilise t.nature_code au lieu de t.titre
    const colorTheme = getTodoColor(t.nature_code);

    // Les classes complètes pour que Tailwind puisse les compiler
    const themeColors = {
      blue: { container: 'bg-white border-blue-700 hover:bg-blue-50 cursor-pointer', text: 'text-blue-700' },
      purple: { container: 'bg-white border-purple-700 hover:bg-purple-50 cursor-pointer', text: 'text-purple-700' },
      orange: { container: 'bg-white border-orange-700 hover:bg-orange-50 cursor-pointer', text: 'text-orange-700' },
      yellow: { container: 'bg-white border-yellow-700 hover:bg-yellow-50 cursor-pointer', text: 'text-yellow-700' },
      pink: { container: 'bg-white border-pink-700 hover:bg-pink-50 cursor-pointer', text: 'text-pink-700' },
      indigo: { container: 'bg-white border-indigo-700 hover:bg-indigo-50 cursor-pointer', text: 'text-indigo-700' },
      teal: { container: 'bg-white border-teal-700 hover:bg-teal-50 cursor-pointer', text: 'text-teal-700' },
      green: { container: 'bg-white border-green-700 hover:bg-green-50 cursor-pointer', text: 'text-green-700' },
    };

    // Style par défaut (aucune couleur spécifique)
    let containerClass = 'bg-white border-slate-200 hover:bg-slate-50 cursor-pointer';
    let titleClass = 'text-sm text-slate-900';

    // Logique de priorité : Terminé > Supprimé > Urgent > Couleur du JSON
    if (t.statut.code === 'termine') {
      containerClass = 'bg-green-50 border-slate-200';
    } else if (t.statut.code === 'supprime') {
      containerClass = 'bg-red-50 border-slate-200';
    } else if (t.urgence) {
      containerClass = 'bg-red-50 border-red-400 hover:bg-red-100 cursor-pointer';
      titleClass = 'text-base font-semibold text-red-800';
    } else if (colorTheme && themeColors[colorTheme]) {
      // Application de la couleur (Fond blanc, Bord 700, Hover 50, Texte 700)
      containerClass = themeColors[colorTheme].container;
      titleClass = `text-sm font-medium ${themeColors[colorTheme].text}`; // font-medium pour faire ressortir un peu la couleur
    }

    return (
      <div
        className={`rounded-xl border p-3 transition-colors ${containerClass}`}
        onClick={() => setDetailId(t.id)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs text-slate-500">
              {`${showDate(t.created_at)}\n${showTime(t.created_at)}`}
              {t.urgence && <span className="text-base font-bold text-red-600"> · URGENT</span>}
            </div>
            {/* Le titre applique dynamiquement la couleur 700 ou reste gris/rouge */}
            <div 
              className={`mt-1 ${titleClass}`} 
              style={clamp2Style()}
            >
              {todoTitre(t)}
            </div>
            {(t.cibles || []).length > 0 && (
              <div className="mt-1 text-xs text-slate-500">
                Pour{' '}
                {(t.cibles || [])
                  .map((c) => {
                    const roleLabel = ROLE_LABELS[c.role] || c.role;
                    const etabLabel = c.etablissement_libelle || c.etablissement;
                    return etabLabel ? `${roleLabel} (${etabLabel})` : roleLabel;
                  })
                  .join(', ')}
              </div>
            )}
            {(t.profilValidateur?.prenom || t.profilValidateur?.nom) && (
              <div className="mt-1 text-xs text-slate-500">
                Validé par{' '}
                <span className="font-medium">{`${t.profilValidateur.prenom || ''} ${t.profilValidateur.nom || ''}`}</span>
              </div>
            )}
          </div>

          {t.statut.code === 'nouveau' ? (
            <div className="flex gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
              <button
                className="p-2 rounded-lg border border-emerald-200 bg-white text-emerald-600 hover:bg-emerald-50 hover:border-emerald-300 transition"
                title="Terminer"
                aria-label="Terminer"
                onClick={() => actDone(t.id)}
              >
                <Check className="h-4 w-4" />
              </button>

              <button
                className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition"
                title="Réaffecter"
                aria-label="Réaffecter"
                onClick={() => setReassignTodo(t)}
              >
                <UserRound className="h-4 w-4" />
              </button>

              {isCreator && (
                <button
                  className="p-2 rounded-lg border border-red-200 bg-white text-red-600 hover:bg-red-50 hover:border-red-300 transition"
                  title="Supprimer"
                  aria-label="Supprimer"
                  onClick={() => actCancel(t.id)}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ) : (
            <div>{t.statut.libelle}</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <PageLayout title="To Do">
      {(isAdmin || isSecretaire) && (
        <div className="px-4 pt-3 flex flex-wrap items-center gap-4">
          {isAdmin && (
            <div className="inline-flex rounded-xl border bg-white overflow-hidden">
              <button
                className={`px-3 py-2 text-sm ${scope === 'mine' ? 'bg-slate-100 font-medium' : ''}`}
                onClick={() => setScope('mine')}
              >
                Moi
              </button>
              <button
                className={`px-3 py-2 text-sm ${scope === 'all' ? 'bg-slate-100 font-medium' : ''}`}
                onClick={() => setScope('all')}
              >
                Tous
              </button>
            </div>
          )}
          {isSecretaire && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">Filtrer cibles :</span>
              {['medecin', 'infirmiere', 'secretaire'].map((r) => (
                <label key={r} className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={rolesFilter.includes(r)}
                    onChange={() => toggleRoleFilter(r)}
                  />
                  {ROLE_LABELS[r]}
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {err && (
        <div className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {err}
        </div>
      )}

      <h1>Actions en cours:</h1>
      <main className="flex-grow overflow-auto px-4 py-3 space-y-3">
        {loading ? (
          <div className="text-slate-500">Chargement…</div>
        ) : err ? (
          <div className="text-slate-500">Impossible d'afficher les To Do.</div>
        ) : currentTodos.length === 0 ? (
          <div className="text-slate-500">Aucune action en cours</div>
        ) : (
          currentTodos.map((t) => <TodoCard key={t.id} t={t} />)
        )}
      </main>

      <button onClick={() => setHistoryShown(!historyShown)}>
        {historyShown ? "- Cacher l'historique" : "+ Afficher l'historique"}
      </button>
      {historyShown && (
        <main className="flex-grow overflow-auto px-4 py-3 space-y-3">
          {loading ? (
            <div className="text-slate-500">Chargement…</div>
          ) : historyTodos.length === 0 ? (
            <div className="text-slate-500">Aucune action en historique</div>
          ) : (
            historyTodos.map((t) => <TodoCard key={t.id} t={t} />)
          )}
        </main>
      )}

      <TodoDetailModal
        open={detailId != null}
        todoId={detailId}
        profileId={profileId}
        onClose={() => setDetailId(null)}
        onDone={load}
        onReassign={(todoToReassign) => setReassignTodo(todoToReassign)}
      />
      <TodoReassignModal
        open={!!reassignTodo}
        todo={reassignTodo}
        profileId={profileId}
        onClose={() => setReassignTodo(null)}
        onDone={load}
      />
    </PageLayout>
  );
}